/**
 * NATO unit symbols, generated from the standard rather than drawn by hand.
 *
 * ── WHY THIS FILE WAS REPLACED ──────────────────────────────────────────────
 * The previous version drew rectangles and diagonals that followed APP-6
 * "loosely rather than exactly", which at least was honest about being an
 * approximation. The owner's requirement on 2026-09-09 was **correct** NATO
 * symbols, and approximate is a different thing from correct. Copying shapes off
 * a symbol gallery — by me or by him — is the wrong method for that requirement.
 *
 * So the drawing is delegated to `milsymbol`, which generates from MIL-STD-2525
 * and STANAG APP-6 (MIT licensed; standards and licence read from its README on
 * 2026-09-09). This file now only chooses colours and sizes.
 *
 * ── THE SEPARATION STILL HOLDS ──────────────────────────────────────────────
 * The simulation names a formation and emits its SIDC; this module turns a SIDC
 * into pixels and knows nothing about rules. The renderer got simpler, not more
 * entangled.
 *
 * ── WHAT WAS CHECKED RATHER THAN ASSUMED ────────────────────────────────────
 * That milsymbol renders **echelon amplifiers** was a hypothesis, not a
 * documented feature — its README does not mention them. It was tested before
 * this file was written: thirteen echelon codes produced thirteen distinct
 * symbols, and the generated markup showed one bar for a company, two for a
 * battalion, three for a regiment, one X for a brigade, two for a division and
 * three for a corps. That is the standard ladder, and it contradicted an
 * automated reading of the Wikimedia gallery — which is exactly why the check
 * was run instead of trusted. `test/symbols.mjs` keeps it running so a library
 * upgrade cannot quietly drop the feature.
 */

import ms from "milsymbol";

const FRIENDLY = "#4d94e0";
const SELECTED = "#8fc4f5";
const HOSTILE = "#e0685d";

// ── WHOSE FORMATION IT IS ───────────────────────────────────────────────────
// Owner, 2026-09-11: *"Mina enheter går inte att särskilja från motståndarens.
// Kan vi ha ljust färgad bakgrund på enheter som påvisar ägarskap?"*
//
// The server emitted identity 3 (friend) for every formation, and every
// identity was given the same pale fill — so the two sides were pixel-identical.
// Ownership is now the light fill — the standard's own light palette, read off
// milsymbol's output on 2026-09-11 rather than picked by eye.
//
// ── FRAMES: WHOSE FOR FORMATIONS ────────────────────────────────────────────
// Owner, 2026-09-11 — three decisions in one day, kept in order so the next
// reader does not reopen them:
//  1. morning: the standard — friend rectangle, hostile diamond;
//  2. midday: *"Alla nationers enheter ska vara rektanglar, använd romb för
//     byggnader"* — shape for kind, fill for owner;
//  3. afternoon: *"Återgå till romb för fiende, rektangel för spelaren och
//     pentagon för byggnader."* — formations back to (1). Buildings got a
//     frame of their own, option C: see "a pentagon of our own" below.
// The fill carries ownership in every version, so it stays.
const FRAME_MINE = "3"; // rectangle
const FRAME_THEIRS = "6"; // diamond
const FILL_FRIEND = "#80e0ff";
const FILL_HOSTILE = "#ff8080";

// ── STRENGTH, WHEN THERE IS SOMETHING TO SAY ────────────────────────────────
// Owner, 2026-09-10: *"Unit strength ska stå under enhetstyp … men bara visas
// när den är under 100%. Färgkoda denna."*
//
// A full-strength formation says nothing, which is the point: a number every
// symbol carries is read as decoration, and one that appears only on the hurt
// ones is read as a warning. The thresholds are wide on purpose — three states
// are legible at 30 px on a phone, five are not.
const STRENGTH_HEALTHY = "#7fd18a"; // 75–100: took losses, still a formation
const STRENGTH_HURT = "#e8c15a"; //   40–75: degraded
const STRENGTH_CRITICAL = "#e8705f"; // under 40: combat-ineffective

/// Cyan, and deliberately not amber.
///
/// The first attempt used `#e8c15a` with a comment claiming it was used nowhere
/// else — which was **exactly** `STRENGTH_HURT`. A badge in the same colour as
/// "this formation is degraded" is worse than no badge; the test that compares
/// the two caught it. Cyan appears on no other symbol element: not affiliation
/// (blue/red), not condition (green/amber/red), not contested ground (amber).
const MOVING_BADGE = "#63d3de";

/// Violet, for a formation resting itself back into shape.
///
/// Owner, 2026-09-17: *"Skapa en badge för enheter som 'vilar' och bygger
/// readiness."* Chosen the way the cyan one had to be chosen after it was first
/// drawn in `STRENGTH_HURT` by accident: against the whole palette, not by eye.
/// Violet appears nowhere else — not ownership (blue/red), not condition
/// (green/amber/red), not movement (cyan), not combat (warm white), not
/// contested ground (amber).
const RESTING_BADGE = "#b48ef0";

// ── THE 2026-09-25 BADGES ───────────────────────────────────────────────────
// Owner, 2026-09-25: *"Skapa badges som påvisar stance, low ammo, low fuel,
// retreating, holding, marsching."* Chosen against the whole palette the way
// the two above were: not ownership, not condition, not an army fill, not
// cyan movement, violet resting or warm-white combat.
//
// Where they sit, one state per edge so none collides:
//   top-left     combat / resting                      (unchanged)
//   top-right    marching / retreating / holding        — what it is doing
//   bottom-left  stance, when it is not Balanced
//   bottom-right low fuel and/or low ammunition
// Marching keeps the cyan chevron it has had since 2026-09-10.

/// Pink, and a double chevron pointing back: pulling back is not marching.
export const RETREAT_BADGE = "#f08cc4";
/// Dark slate with a light stop mark: standing where it is. Quiet on purpose
/// — it is the commonest state on the map.
export const HOLDING_BADGE = "#2b3440";
/// Dark plate for the stance; the glyph carries which stance.
export const STANCE_BADGE = "#1d2530";
/// Orange: running short. Not amber, which is `STRENGTH_HURT`.
export const LOW_SUPPLY_BADGE = "#ff9b54";

/// Below this share of a full load, fuel or ammunition is drawn as low.
///
/// Display only; the server's rules are `sustainment::may_march` (no fuel) and
/// `combat::may_attack` (no ammunition when cut). A quarter is about nine hours
/// of marching (`ENDURANCE_HOURS` 36) or three of fighting
/// (`COMBAT_ENDURANCE_HOURS` 12) — warning enough to act on. First-playtest
/// figure, mine rather than the owner's.
export const LOW_CARRIED = 0.25;

/** What a formation is doing, as the menu's head line says it. */
export type Movement = "marching" | "retreating" | "holding";

/** Own formations only: an enemy's progress and retreat are withheld at every
 *  grade, so for one there is nothing honest to draw. */
export function movementOf(u: {
  mine: boolean;
  progress: number | null;
  withdrawing: boolean;
  engaged: boolean;
}): Movement | undefined {
  if (!u.mine) return undefined;
  if (u.withdrawing) return "retreating";
  if (u.progress !== null) return "marching";
  // Standing in a fight is fighting, which the combat badge already says.
  return u.engaged ? undefined : "holding";
}

/** The badges a formation carries, decided once for the symbol and the hover
 *  card alike — two copies of the rule would drift, and a card explaining a
 *  badge the symbol does not show is worse than no card. */
export interface Status {
  engaged: boolean;
  resting: boolean;
  movement?: Movement;
  stance: string;
  lowFuel: boolean;
  lowAmmo: boolean;
}

export function statusOf(u: {
  mine: boolean;
  progress: number | null;
  withdrawing: boolean;
  engaged: boolean;
  supply: string;
  readiness: number;
  combat_stance: string;
  fuel?: number;
  ammunition?: number;
}): Status {
  const low = lowSupplyOf(u);
  return {
    engaged: u.engaged,
    // Own formations only. An identified enemy's progress is withheld, so
    // `isResting` would read a marching enemy as standing still.
    resting: u.mine && isResting(u),
    movement: movementOf(u),
    stance: u.combat_stance,
    lowFuel: low.fuel,
    lowAmmo: low.ammunition,
  };
}

/** One line of the hover card: the badge's colour and what it means. */
export interface BadgeNote {
  badge: string;
  colour: string;
  text: string;
}

/**
 * What each badge on this formation means, in words.
 *
 * Owner, 2026-09-25: *"När jag hovrar över en enhet vill jag att statusikon
 * som är satt på enhet förklaras med text."* The stance figures are the same
 * ones the unit menu states; the rules behind the others are `sustainment`
 * and `orders::march_to`.
 */
export function explainStatus(
  s: Status,
  carried: { fuel?: number; ammunition?: number } = {},
): BadgeNote[] {
  const pct = (v?: number) => `${Math.round((v ?? 1) * 100)}%`;
  const out: BadgeNote[] = [];
  if (s.engaged) {
    out.push({ badge: "combat", colour: COMBAT_BADGE, text: "In combat — it can only move away from the enemy it is fighting." });
  } else if (s.resting) {
    out.push({ badge: "resting", colour: RESTING_BADGE, text: "Resting — supplied and standing still, rebuilding readiness." });
  }
  if (s.movement === "marching") {
    out.push({ badge: "marching", colour: MOVING_BADGE, text: "Marching — under orders; readiness wears after 8 hours on the move." });
  } else if (s.movement === "retreating") {
    out.push({ badge: "retreating", colour: RETREAT_BADGE, text: "Retreating — pulling back at 60% of march pace, after an hour to disengage." });
  } else if (s.movement === "holding") {
    out.push({ badge: "holding", colour: HOLDING_BADGE, text: "Holding — standing where it was put; formations that stand still dig in." });
  }
  const stance: Record<string, string> = {
    assault: "Assault — +20% attack, −15% defence; turns a standoff into an attack.",
    hold_ground: "Hold ground — +20% defence, −15% attack.",
    delay: "Delay — +10% defence, −15% attack, half the time to disengage.",
  };
  if (stance[s.stance]) out.push({ badge: "stance", colour: STANCE_BADGE, text: stance[s.stance] });
  if (s.lowFuel) {
    out.push({ badge: "low-fuel", colour: LOW_SUPPLY_BADGE, text: `Low fuel — ${pct(carried.fuel)} left; empty, it cannot march until its ground is supplied.` });
  }
  if (s.lowAmmo) {
    out.push({ badge: "low-ammo", colour: LOW_SUPPLY_BADGE, text: `Low ammunition — ${pct(carried.ammunition)} left; empty and cut off, it can only defend.` });
  }
  return out;
}

/** Which loads are low. A redacted enemy arrives with both at 1.0. */
export function lowSupplyOf(u: { fuel?: number; ammunition?: number }): {
  fuel: boolean;
  ammunition: boolean;
} {
  return {
    fuel: (u.fuel ?? 1) < LOW_CARRIED,
    ammunition: (u.ammunition ?? 1) < LOW_CARRIED,
  };
}

// ── NATIONAL COLOURS ────────────────────────────────────────────────────────
// Owner, 2026-09-17: *"Skapa nationsfärger för alla länder men se till att
// lagen hanteras korrekt (så att t.ex. USA och Britterna delar ZOC och inte
// anfaller varandra)."* His palette: grey Italy, military green USA, blue
// Britain — and for the two he had not counted, dark grey-black Germany and
// khaki Canada.
//
// ── THEY REPLACE THE OWNERSHIP FILL, WHICH COSTS NOTHING ────────────────────
// The fill carried ownership. It can stop, because this file already says the
// same thing twice over: *"Mine a rectangle, theirs a diamond — the fill says
// the same thing again."* Whose a formation is survives in the frame shape and
// the stroke; the fill was the redundant copy, so it is free to carry army.
//
// ── THREE OF HIS FIVE HAD TO MOVE, AND THE PALETTE SAYS WHY ─────────────────
// Chosen against every colour already in use, the way the cyan badge had to be
// after it was first drawn in `STRENGTH_HURT` by accident:
//
// · 🔴 **Germany could not be near-black.** `iconColor` and `PLATE` are
//   `#0b1016`, and the branch icon is drawn on top of the fill — a black fill
//   swallows it and leaves a blank frame. Blue-grey keeps the icon legible and
//   still reads as the darker of the two Axis greys.
// · **USA is olive, not mint.** `STRENGTH_HEALTHY` is `#7fd18a`, and green
//   already means "at strength" on the same symbol.
// · **Canada is pale sand, not amber.** `STRENGTH_HURT` is `#e8c15a`, which
//   also colours contested ground.
//
// Italy and Germany are the two that must separate from each other at 26 px on
// a phone, so they differ in lightness rather than in hue.
const NATION_FILL: Record<string, string> = {
  ITA: "#c9cfd6", // light cool grey
  GER: "#6b7785", // blue-grey: darker than Italy, never black
  USA: "#9fb37a", // olive
  GBR: "#7fb3e8", // blue
  CAN: "#e8d3a8", // pale sand
};

/** The fill for an army, or `undefined` when there is nothing to say. */
export function nationFill(nation?: string | null): string | undefined {
  return nation ? NATION_FILL[nation] : undefined;
}

/**
 * Whether a formation is recovering right now.
 *
 * ── THE RULE IS THE SERVER'S, MIRRORED HERE ON PURPOSE ──────────────────────
 * `sustainment::recover` refills readiness, fuel and ammunition only when a
 * formation is **supplied, standing still, and not fighting**. The badge has to
 * say the same thing, so the conditions are copied from that function rather
 * than invented for the map.
 *
 * ⚠️ `"strained"` counts as supplied — the server tests `supply != "cut"`, not
 * `supply == "supplied"`. Guessing the stricter reading would have hidden the
 * badge on formations that are in fact recovering.
 *
 * ⚠️ `readiness < 1` is the visible half of a larger truth. Recovery also refills
 * fuel and ammunition, which the unit feed does not carry, so a formation at full
 * readiness with an empty tank is recovering without showing it. The owner asked
 * for *"vilar och bygger readiness"*, so readiness is what the badge tracks —
 * stated here because the gap is real rather than overlooked.
 *
 * Takes primitives, not a `Unit`: this module knows about symbols, not about the
 * API's shapes, and a plain signature is what lets the test file exercise it.
 */
export function isResting(u: {
  supply: string;
  progress: number | null;
  engaged: boolean;
  readiness: number;
}): boolean {
  return u.supply !== "cut" && u.progress === null && !u.engaged && u.readiness < 1;
}

/** The colour a strength figure is drawn in. Exported so the panel and the
 *  map cannot drift into two different scales. */
export function strengthColour(strength: number): string {
  if (strength >= 0.75) return STRENGTH_HEALTHY;
  if (strength >= 0.4) return STRENGTH_HURT;
  return STRENGTH_CRITICAL;
}

export type Affiliation = "friendly" | "hostile";

/** The light fill that says whose a symbol is. Relative to the viewer — the
 *  same company is friendly to one player and hostile to the other — so it is
 *  decided at draw time from `mine`, never baked in by the server. */
export function ownershipFill(affiliation: Affiliation = "friendly"): string {
  return affiliation === "hostile" ? FILL_HOSTILE : FILL_FRIEND;
}

/** Force the frame digit (SIDC position 4). See "frames" above. */
function withFrame(sidc: string, frame: string): string {
  if (sidc.length < 20) return sidc;
  return sidc.slice(0, 3) + frame + sidc.slice(4);
}

export interface SymbolOptions {
  selected?: boolean;
  moving?: boolean;
  affiliation?: Affiliation;
  size?: number;
  /** Plain-language caption drawn under the frame, e.g. "Recon company".
   *  An array draws one line per entry. */
  label?: string | string[];
  /** 1.0 is whole. Below it the standard's *damaged* condition is drawn — a
   *  bar under the frame — so a formation that needs refitting says so on the
   *  map rather than only inside a menu nobody has opened yet. */
  strength?: number;
  /** In a fight: drawn with the combat badge. */
  engaged?: boolean;
  /** Marching, retreating or holding, top-right. Supersedes `moving`. */
  movement?: Movement;
  /** `assault`, `hold_ground` or `delay` draws a badge; `balanced` and a
   *  withheld stance draw none. */
  stance?: string;
  /** Fuel or ammunition under `LOW_CARRIED`. */
  lowFuel?: boolean;
  lowAmmo?: boolean;
  /** Resting in supply and rebuilding readiness: drawn with the resting badge.
   *  Mutually exclusive with `engaged` by the simulation's own rule, which is
   *  why the two share a corner. See `isResting`. */
  resting?: boolean;
  /** Which army: `GER` / `ITA` / `USA` / `GBR` / `CAN`. Drawn as the fill, and
   *  falls back to the ownership fill when absent — which is what an
   *  unidentified enemy looks like, since the server withholds its army. */
  nation?: string | null;
}

/**
 * Frame height in pixels when nothing says otherwise.
 *
 * ── WHY THERE ARE TWO SIZES AND NOT ONE ─────────────────────────────────────
 * Owner, 2026-09-09: *"Enheter syns jättebra på telefon … men är för litet på
 * dator."* Both readings are correct, and that is the point: a phone shows
 * roughly 390 CSS pixels across a hand-width of glass, a desktop shows 1600
 * across an arm's length of it. The same 26-pixel symbol is a thumbnail on one
 * and a postage stamp on the other.
 *
 * So the size is chosen from the viewport rather than fixed. Done by generating
 * a larger symbol rather than by scaling the small one in CSS: the marker's
 * anchor is computed from the element's real dimensions, and a CSS transform
 * changes what you see without changing those — which puts the symbol and the
 * point it marks in two different places.
 */
export const DEFAULT_SIZE = 26;
/** The same symbol on a screen you sit back from. */
export const DESKTOP_SIZE = 40;

/** Pick a symbol size for the space available. */
export function symbolSizeFor(viewportWidthPx: number): number {
  return viewportWidthPx >= 900 ? DESKTOP_SIZE : DEFAULT_SIZE;
}

/** Dark plate behind the echelon marks and the caption. */
const PLATE = "#0b1016";
/** Text and echelon strokes drawn on that plate. */
const PLATE_INK = "#e8eef5";

/**
 * Render a symbol from its SIDC.
 *
 * The code is built by the server (`Formation::sidc`) so branch and echelon are
 * decided once, in the simulation, rather than reconstructed from a label here.
 */
export function symbolFromSidc(sidc: string, opts: SymbolOptions = {}): string {
  const stroke = opts.selected ? SELECTED : opts.affiliation === "hostile" ? HOSTILE : FRIENDLY;

  // Mine a rectangle, theirs a diamond — the fill says the same thing again.
  const owned = withFrame(sidc, opts.affiliation === "hostile" ? FRAME_THEIRS : FRAME_MINE);
  // Army first, ownership as the fallback. An unidentified enemy has no army to
  // draw — the server withholds it — so it keeps the old hostile fill, which is
  // the honest picture: something hostile is there and you do not know whose.
  const fill = nationFill(opts.nation) ?? ownershipFill(opts.affiliation);

  // ── NO DAMAGE BAR ───────────────────────────────────────────────────────
  // Owner, 2026-09-12, on the bar under a hurt formation being yellow whatever
  // its strength: *"Den kan tas bort helt. Vi ska lägga till annat senare."*
  //
  // He is right that it carried almost nothing: the standard's *damaged*
  // condition is one bar with no degrees, so it said "hurt" and never how
  // badly, while sitting directly above a figure that says exactly how badly.
  // The figure stays — that was a separate instruction of his on 2026-09-10
  // (*"Unit strength ska stå under enhetstyp"*) and it is still in force.
  //
  // `damaged` therefore now decides only whether the percentage is captioned,
  // not what the symbol looks like.
  const damaged = opts.strength !== undefined && opts.strength < 0.999;
  const code = owned;

  const symbol = new ms.Symbol(code, {
    size: opts.size ?? DEFAULT_SIZE,
    // A light fill rather than a transparent frame. On a dark map — and even
    // more so over satellite imagery — an unfilled outline disappears against
    // whatever happens to be behind it. A filled frame is also what the standard
    // actually specifies; transparency was the shortcut, not the convention.
    fill: true,
    fillOpacity: 1,
    colorMode: { Civilian: fill, Friend: fill, Hostile: fill, Neutral: fill, Suspect: fill, Unknown: fill },
    // The branch icon is near-black, as the standard draws it. It used to carry
    // the side's colour — which was the only thing separating the sides, and a
    // blue icon on the light-blue friendly fill would barely read. The fill
    // carries ownership now, so selection moves to a light outline.
    iconColor: "#0b1016",
    outlineColor: opts.selected ? SELECTED : "#0b1016",
    outlineWidth: opts.selected ? 3 : 2,
    strokeWidth: opts.selected ? 5 : 4,
  });

  if (!symbol.isValid()) {
    // A bad code has to be visible rather than silently absent: an invisible
    // unit is a far worse failure than an ugly one.
    return fallback(stroke);
  }

  let svg = symbol.asSVG();
  // Owner, 2026-09-18: echelon above the symbol (for example XX), the short
  // formation name below (for example British 50th). This reverses the
  // previous day's arrangement, which removed the standard amplifier and put
  // the name in its place.
  svg = plateTheEchelon(svg);
  // Where the frame ends, read before anything is drawn on top rather than
  // assumed. Buildings share the rule and are diamonds, which reach ~22 units
  // lower than a rectangle. (This used to have to clear a damage bar at y
  // 155-180 as well; the bar is gone, but reading the frame is still right —
  // the offset was measured once and drifted the moment the shape changed.)
  const captionTop = frameBottom(svg) + 4;
  // The description first, then its condition underneath — and the condition
  // only when there is one. See `strengthColour`.
  const caption: (string | CaptionLine)[] = [];
  if (opts.label) caption.push(...(Array.isArray(opts.label) ? opts.label : [opts.label]));
  if (damaged && opts.strength !== undefined && caption.length > 0) {
    caption.push({
      text: `${Math.round(opts.strength * 100)}%`,
      fill: strengthColour(opts.strength),
    });
  }
  if (caption.length > 0) svg = captionBelow(svg, caption, captionTop);
  const movement = opts.movement ?? (opts.moving ? "marching" : undefined);
  let moved =
    movement === "marching"
      ? withMovementPip(svg, stroke)
      : movement === "retreating"
        ? withRetreatBadge(svg)
        : movement === "holding"
          ? withHoldingBadge(svg)
          : svg;
  moved = withStanceBadge(moved, opts.stance);
  moved = withLowSupplyBadge(moved, !!opts.lowFuel, !!opts.lowAmmo);
  // Combat wins the corner when both are somehow set: being shot at is the more
  // urgent fact, and the simulation cannot produce the combination anyway —
  // `recover` refuses to run on a formation that is fighting.
  if (opts.engaged) return withCombatBadge(moved);
  return opts.resting ? withRestingBadge(moved) : moved;
}

/** The lowest point of milsymbol's drawing, in its own units, read off the
 *  viewBox. Exported so the tests measure the same thing the map draws. */
export function frameBottom(svg: string): number {
  const m = /viewBox="(-?[\d.]+) (-?[\d.]+) ([\d.]+) ([\d.]+)"/.exec(svg);
  return m ? Number(m[2]) + Number(m[4]) : 154;
}

// ── The status field ────────────────────────────────────────────────────────
// SIDC position 7 (index 6) is status/condition. The server always emits 0
// (present, fully capable) because it emits *what a formation is*; whether that
// formation is hurt or half-built is state, and state changes far more often
// than identity does. Amending the code here keeps the server from having to
// re-derive a symbol every time a shell lands.

// ── THE ECHELON PLATE, AND THE LESSON THAT KEPT IT VISIBLE ──────────────────
//
// Owner, 2026-09-17 temporarily put the formation name here instead. The owner
// reversed that on 2026-09-18: echelon above, short identity below. The failed
// version remains part of the decision history, not part of the renderer.
//
// 🔴 THE PART WORTH KEEPING, because it cost a silent failure: **milsymbol
// emits the echelon twice** — an outline pass first, whose child paths carry
// their own dark stroke, and the visible pass after it, whose paths inherit the
// group's stroke. Both are `<g transform="translate(0,0)">`. Recolouring the
// *first* match relights the invisible outline while the visible marks stay
// black on a black plate, so the echelon disappears entirely — and a test that
// only asserts *a* group was relit passes while it happens. The test below now
// checks the visible pass and the shipped call site.

/**
 * Put a dark plate behind milsymbol's echelon and relight the marks.
 *
 * Owner, 2026-09-18: *"Jag vill ha echelon nivå över symbol (t.ex. XX)"*.
 * This function was superseded for one day while the formation name occupied
 * that position. Its tested copy survived in `web/test/symbol-helpers.mjs`, so
 * restoring the standard amplifier did not mean reconstructing it from memory.
 *
 * 🔴 milsymbol emits the echelon twice: an outline pass first, whose child
 * paths carry their own dark stroke, and the visible pass after it, whose paths
 * inherit the group's stroke. Recolouring the first match makes a test green
 * while the visible marks remain black on a black plate. The last group is the
 * one the player sees, and every explicit child stroke must be relit too.
 */
function plateTheEchelon(svg: string): string {
  const groups = [
    ...svg.matchAll(/<g transform="translate\(0,0\)"([^>]*)>([\s\S]*?)<\/g>/g),
  ];
  if (groups.length === 0) return svg;

  const visible = groups[groups.length - 1];
  const body = visible[2];
  const anchors = [...body.matchAll(/[ML]\s*(-?[\d.]+),(-?[\d.]+)/g)];
  if (anchors.length === 0) return svg;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const m of anchors) {
    const x = Number(m[1]);
    const y = Number(m[2]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  // X motifs are relative offsets from their anchor; bars are absolute. A
  // company bar has zero raw width, so it also needs a minimum plate width.
  if (/l ?25,-25/.test(body)) {
    maxX += 25;
    minY -= 25;
  }
  const pad = 7;
  const MIN_PLATE_W = 46;
  const w = Math.max(maxX - minX + pad * 2, MIN_PLATE_W);
  const centreX = (minX + maxX) / 2;
  const plate =
    `<rect x="${centreX - w / 2}" y="${minY - pad}" ` +
    `width="${w}" height="${maxY - minY + pad * 2}" ` +
    `fill="${PLATE}" fill-opacity="0.92" />`;

  const relitBody = body.replace(/stroke="[^"]*"/g, `stroke="${PLATE_INK}"`);
  const relit =
    `<g transform="translate(0,0)"${visible[1].replace(
      /stroke="[^"]*"/,
      `stroke="${PLATE_INK}"`,
    )}>${relitBody}</g>`;

  // The plate covers the dark outline pass and sits immediately behind the
  // visible pass, rather than covering the mark it is meant to reveal.
  return svg.replace(visible[0], plate + relit);
}

/**
 * A plain-language caption under the frame, light on dark.
 *
 * milsymbol can place text amplifiers, but the standard puts them beside the
 * symbol — the label came out to the left and pushed the frame off-centre. What
 * was asked for is a caption underneath, so it is drawn here instead of fought
 * for there.
 *
 * The viewBox is extended downward to make room. Widening it too, when the text
 * is broader than the symbol, is what stops a long name from being clipped.
 */
/** A caption line, optionally in its own colour. */
export interface CaptionLine {
  text: string;
  fill?: string;
}

function captionBelow(
  svg: string,
  label: string | (string | CaptionLine)[],
  top = 158,
): string {
  const vb = /viewBox="(-?[\d.]+) (-?[\d.]+) ([\d.]+) ([\d.]+)"/.exec(svg);
  const wh = /<svg[^>]*width="([\d.]+)" height="([\d.]+)"/.exec(svg);
  if (!vb || !wh) return svg;

  const [minX, minY, w, h] = [Number(vb[1]), Number(vb[2]), Number(vb[3]), Number(vb[4])];

  const lines: CaptionLine[] = (Array.isArray(label) ? label : [label])
    .map((l) => (typeof l === "string" ? { text: l } : l))
    .filter((l) => l.text.length > 0);
  if (lines.length === 0) return svg;

  const fontSize = 30;
  const lineH = 38;
  const boxH = lineH * lines.length;
  // ── THE PLATE WAS FAR WIDER THAN THE TEXT ──────────────────────────────────
  // Owner, 2026-09-10: *"Det är ett mörkt område under HQ battalion-texten.
  // varför?"*
  //
  // Measured: for "HQ battalion" the plate came out **218 units wide against a
  // symbol 150 wide** — 45% wider than the thing it captions — and
  // `.unit-marker` puts a drop-shadow under the lot. That reads as a dark slab,
  // which is exactly what he saw.
  //
  // Two causes, both here. `0.55 em` per character over-estimates Arial: it is
  // right for capitals and much too generous for lower case, and these captions
  // are mostly lower case. And `Math.max(textW, 120)` imposed a minimum width
  // on captions that did not need one. Neither had a reason beyond caution.
  const widest = lines.reduce((n, l) => Math.max(n, l.text.length), 0);
  const boxW = widest * fontSize * 0.46 + 12;
  const centre = 100; // the frame's centre in milsymbol's internal space

  const newMinX = Math.min(minX, centre - boxW / 2 - 4);
  const newW = Math.max(w, Math.max(minX + w, centre + boxW / 2 + 4) - newMinX);
  const newH = Math.max(h, top + boxH + 6 - minY);

  const scale = Number(wh[1]) / w;
  const caption =
    `<rect x="${centre - boxW / 2}" y="${top}" width="${boxW}" height="${boxH}" ` +
    `rx="6" fill="${PLATE}" fill-opacity="0.86" />` +
    lines
      .map(
        (line, i) =>
          `<text x="${centre}" y="${top + lineH * i + lineH / 2}" text-anchor="middle" ` +
          `dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" ` +
          `font-size="${fontSize}" fill="${line.fill ?? PLATE_INK}">` +
          `${escapeXml(line.text)}</text>`,
      )
      .join("");

  return svg
    .replace(/viewBox="[^"]*"/, `viewBox="${newMinX} ${minY} ${newW} ${newH}"`)
    .replace(/width="[\d.]+" height="[\d.]+"/, `width="${newW * scale}" height="${newH * scale}"`)
    .replace(/<\/svg>\s*$/, `${caption}</svg>`);
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"]/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c] as string,
  );
}

/** Marks a unit under orders. Not part of the standard, so it is added here. */
/**
 * The badge that says a formation is under way.
 *
 * ── WHY A BADGE AND NOT THE OLD DOT ─────────────────────────────────────────
 * Owner, 2026-09-10: *"Skapa en bricka (badge) som påvisar att enhet är under
 * förflyttning."*
 *
 * There *was* a marker — a 6 px filled circle in the top-left corner — and it
 * failed at the one job it had. It was the same colour as the symbol's own
 * frame, roughly the size of an echelon bar, and sat where nothing else drew,
 * so it read as part of the symbol rather than as a state. Asking for a badge
 * after looking at a map full of them is the evidence: it was not legible.
 *
 * What makes this one read as a badge instead:
 *   · **its own shape** — a rounded plate, not a dot that could be an amplifier
 *   · **its own colour** — amber, used nowhere else on a friendly symbol, so it
 *     cannot be mistaken for affiliation or condition
 *   · **an arrow inside it**, which says *movement* rather than merely *marked*
 *   · a dark outline, so it survives on a satellite photograph
 *
 * Top-right, because strength sits underneath and the echelon sits on top: the
 * three states a formation reports each have their own edge and never collide.
 */
/**
 * Owner, 2026-09-11: *"Att enheter strider ska visas med badge och visuellt på
 * spelplan."* Top-left, opposite the movement badge — a formation being fired on
 * while it pulls back carries both. Warm white with crossed strokes: a colour no
 * other symbol state uses (not ownership, strength, movement or contested ground).
 */
export const COMBAT_BADGE = "#f2ede4";

function withCombatBadge(svg: string): string {
  const badge =
    `<g data-badge="combat">` +
    `<rect x="22" y="26" width="54" height="34" rx="8" ` +
    `fill="${COMBAT_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    `<path d="M38 34 L60 52 M60 34 L38 52" fill="none" stroke="#b3261e" ` +
    `stroke-width="6" stroke-linecap="round" />` +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

/**
 * The badge that says a formation is resting itself back into shape.
 *
 * Top-left, the same corner as the combat badge — and safe there because the two
 * cannot both be true: `sustainment::recover` refuses to run on a formation that
 * is fighting. Movement and combat *can* coexist (a formation pulling back under
 * fire carries both), which is why those two have separate corners and this one
 * does not need a third.
 *
 * Two upright bars rather than an arrow or a cross: it reads as *paused*, which
 * is what resting is — the formation is doing nothing on purpose.
 */
function withRestingBadge(svg: string): string {
  const badge =
    `<g data-badge="resting">` +
    `<rect x="22" y="26" width="54" height="34" rx="8" ` +
    `fill="${RESTING_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    `<path d="M42 35 L42 51 M56 35 L56 51" fill="none" stroke="#1b1205" ` +
    `stroke-width="6" stroke-linecap="round" />` +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

function withMovementPip(svg: string, _stroke: string): string {
  const badge =
    // Plate. Positioned in milsymbol's internal space, where the frame spans
    // roughly x 50–150 and y 50–150.
    `<g>` +
    `<rect x="128" y="26" width="54" height="34" rx="8" ` +
    `fill="${MOVING_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    // A chevron pointing the way, not a triangle: an arrowhead alone reads as
    // direction, and this badge is about *being* in motion, not which heading.
    `<path d="M144 43 L156 43 M152 37 L160 43 L152 49" ` +
    `fill="none" stroke="#1b1205" stroke-width="5" ` +
    `stroke-linecap="round" stroke-linejoin="round" />` +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

/** Top-right, in the marching badge's place: a double chevron pointing back. */
function withRetreatBadge(svg: string): string {
  const badge =
    `<g data-badge="retreating">` +
    `<rect x="128" y="26" width="54" height="34" rx="8" ` +
    `fill="${RETREAT_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    `<path d="M152 35 L143 43 L152 51 M165 35 L156 43 L165 51" ` +
    `fill="none" stroke="#1b1205" stroke-width="5" ` +
    `stroke-linecap="round" stroke-linejoin="round" />` +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

/** Top-right: a stop mark. Standing where it was put. */
function withHoldingBadge(svg: string): string {
  const badge =
    `<g data-badge="holding">` +
    `<rect x="128" y="26" width="54" height="34" rx="8" ` +
    `fill="${HOLDING_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    `<rect x="147" y="35" width="16" height="16" rx="2" fill="${PLATE_INK}" />` +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

/**
 * Bottom-left: the combat stance, when it is anything but Balanced.
 *
 * Balanced says nothing, the way full strength says nothing: a badge every
 * formation carries is decoration. Glyphs rather than letters — a letter is
 * five pixels tall on a phone. Assault an arrowhead forward, Hold ground a
 * wall, Delay an hourglass.
 */
function withStanceBadge(svg: string, stance?: string): string {
  const glyph =
    stance === "assault"
      ? `<path d="M49 146 L49 128 M40 136 L49 126 L58 136" fill="none" stroke="#ff7a59" ` +
        `stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />`
      : stance === "hold_ground"
        ? `<path d="M36 142 L62 142 M38 142 L38 128 M49 142 L49 128 M60 142 L60 128" ` +
          `fill="none" stroke="#9dc6ef" stroke-width="5" stroke-linecap="round" />`
        : stance === "delay"
          ? `<path d="M39 125 L59 125 L39 145 L59 145 Z" fill="none" stroke="${PLATE_INK}" ` +
            `stroke-width="4" stroke-linejoin="round" />`
          : null;
  if (glyph === null) return svg;
  const badge =
    `<g data-badge="stance" data-stance="${stance}">` +
    `<rect x="22" y="118" width="54" height="34" rx="8" ` +
    `fill="${STANCE_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    glyph +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

/**
 * Bottom-right: running short. A drop for fuel, a cartridge for ammunition;
 * the plate widens leftwards to carry both.
 */
function withLowSupplyBadge(svg: string, fuel: boolean, ammunition: boolean): string {
  if (!fuel && !ammunition) return svg;
  const both = fuel && ammunition;
  const width = both ? 84 : 54;
  const left = 182 - width;
  const drop = (cx: number) =>
    `<path data-glyph="fuel" d="M${cx} 124 C${cx - 10} 136 ${cx - 9} 146 ${cx} 146 ` +
    `C${cx + 9} 146 ${cx + 10} 136 ${cx} 124 Z" fill="#1b1205" />`;
  const cartridge = (cx: number) =>
    `<path data-glyph="ammunition" d="M${cx - 5} 146 L${cx - 5} 132 Q${cx} 121 ${cx + 5} 132 ` +
    `L${cx + 5} 146 Z" fill="#1b1205" />`;
  const glyphs = both
    ? drop(left + 26) + cartridge(left + 58)
    : fuel
      ? drop(left + 27)
      : cartridge(left + 27);
  const badge =
    `<g data-badge="low-supply">` +
    `<rect x="${left}" y="118" width="${width}" height="34" rx="8" ` +
    `fill="${LOW_SUPPLY_BADGE}" stroke="#0b1016" stroke-width="4" />` +
    glyphs +
    `</g>`;
  return svg.replace(/<\/svg>\s*$/, `${badge}</svg>`);
}

function fallback(stroke: string): string {
  return `<svg width="30" height="22" viewBox="0 0 30 22" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="2" y="2" width="26" height="18" fill="none" stroke="${stroke}"
        stroke-width="2" stroke-dasharray="3 2" />
</svg>`;
}

// ── BUILDINGS ───────────────────────────────────────────────────────────────
//
// Owner, 2026-09-09: *"Hur syns byggnader? Hur syns byggnader under
// konstruktion? Hur vet vi hur lång tid det är kvar innan byggnad är byggd?"*
//
// The honest answer was: they did not. Nothing on the client had ever drawn a
// building, finished or otherwise, so a depot that cost 120 materiel and eight
// hours of work produced a line in the log and nothing else.
//
// ── A PENTAGON OF OUR OWN, THE STANDARD'S ICON INSIDE ───────────────────────
// Owner, 2026-09-11: *"… pentagon för byggnader. Skapa symbol i pentagon som
// särskiljer byggnaderna åt."* — then, offered three ways, *"Kör på C."*
//
// MIL-STD-2525D has no pentagon: an installation is drawn in the unit frame
// with a small bar on top, which at 26 px on a phone barely separates it from a
// formation. So the frame is ours. The icon inside is the standard's — symbol
// set 20, Land Installation, drawn by milsymbol without its frame — picked from
// Esri's machine-readable 2525D (JMSML) on 2026-09-11. Four of the ten are the
// nearest type rather than an exact one, marked below, and the standard has no
// fortification at all: that is the one icon drawn by hand.
// The entity-code table and the pentagon went with the building symbol. Both are
// worth recovering verbatim rather than re-derived if block D draws fixed
// installations: the codes were read out of Esri's machine-readable 2525D
// (JMSML), four of the ten were the *nearest* type rather than an exact one, and
// the pentagon's points are in milsymbol's own 200-unit space so captions lined
// up with the formations drawn beside it.

// ── THE BUILDING SYMBOL WENT WITH THE BUILDINGS ─────────────────────────────
//
// `buildingSymbol`, `buildingLabel`, `installationIcon`, the fortification and
// supply-point marks and the pentagon frame drew a structure on the map.
//
// Three things in it were bugs first and apply to any symbol drawn here:
//
// · 🔴 **milsymbol ignores `iconColor` on an unframed, unfilled icon** and draws
//   the standard's cyan — invisible on a light frame. `monoColor` is the one
//   that works. Caught on a screenshot, not by a test, which is why the test
//   that replaced it checks the *colours inside* the icon.
// · The standard keeps its installation bar even without a frame, so it sat on
//   the pentagon's point and had to be stripped by geometry — matched on its
//   fixed corners, because the lower edge moves with the stroke width.
// · Icon lines at the standard's 3 units are a hairline once scaled into a
//   frame on a phone.

/**
 * "3 h 20 m", "45 m", "under a minute".
 *
 * Hours and minutes, never seconds: the smallest thing anyone builds takes
 * hours, and a countdown ticking a second at a time invites watching a progress
 * bar in a game whose whole premise is that you close the browser and come back.
 */
export function formatRemaining(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "any moment";
  const total = Math.round(seconds / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return m <= 0 ? "under a minute" : `${m} m`;
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
}

/**
 * Plain-language name for a formation.
 *
 * ── ONE MAP, ONE LANGUAGE ───────────────────────────────────────────────────
 * English, because the standing decision is an English-language site. The
 * request that prompted this used a Swedish example ("Spaningskompani"), so the
 * wording is kept in these two tables and nowhere else — switching the whole
 * interface to Swedish is an edit to this file alone.
 *
 * The strings are shortened deliberately: this is drawn under a map symbol at
 * about 30 px, and "Reconnaissance company" is twice as wide as the symbol it
 * belongs to.
 */
const BRANCH_NAMES: Record<string, string> = {
  headquarters: "HQ",
  infantry: "Rifle",
  mechanised: "Mech",
  armour: "Armour",
  reconnaissance: "Recon",
  artillery: "Artillery",
  air_defence: "Air def",
  engineer: "Engineer",
  supply: "Supply",
  signal: "Signal",
  fighter: "Fighter",
  ground_attack: "Attack",
  air_recon: "Air recon",
  air_transport: "Transport",
  surface: "Surface",
  patrol: "Patrol",
  submarine: "Submarine",
  amphibious: "Amphibious",
};

const ECHELON_NAMES: Record<string, string> = {
  company: "company",
  battalion: "battalion",
  regiment: "regiment",
  brigade: "brigade",
  division: "division",
  corps: "corps",
  army: "army",
  army_group: "army group",
  front: "front",
};

export function formationLabel(branch: string, echelon: string): string {
  const b = BRANCH_NAMES[branch] ?? branch.replace(/_/g, " ");
  const e = ECHELON_NAMES[echelon] ?? echelon.replace(/_/g, " ");
  return `${b} ${e}`;
}

/**
 * Backwards-compatible entry point for the phase-2 client.
 *
 * The old three-kind vocabulary (`base` / `scout` / `worker`) belonged to the
 * superseded starting force. It is mapped onto real formations here so the
 * existing view keeps working while the rest of the client catches up, rather
 * than breaking the map in the same change that fixed the symbols.
 */
export type LegacyKind = "base" | "scout" | "worker";

/** Build a 20-character MIL-STD-2525D code the same way the server does. */
function sidc(amplifier: string, entity: string): string {
  //  ver  ctx aff  set  st  hq  amp   entity  modifiers
  return "10" + "0" + "3" + "10" + "0" + "0" + amplifier + entity + "0000";
}

const LEGACY_SIDC: Record<LegacyKind, string> = {
  base: sidc("16", "121300"), // headquarters, battalion
  scout: sidc("15", "121800"), // reconnaissance, company
  worker: sidc("15", "121700"), // engineer, company
};

export function unitSymbol(kind: LegacyKind, opts: SymbolOptions = {}): string {
  return symbolFromSidc(LEGACY_SIDC[kind], opts);
}

export function kindLabel(kind: LegacyKind): string {
  switch (kind) {
    case "base":
      return "Headquarters";
    case "scout":
      return "Reconnaissance";
    case "worker":
      return "Engineers";
  }
}
